/**
 * OEM Utility Functions
 * Handles OEM detection, branding application, and domain resolution
 */
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import oemDB, { OEMConfig } from './indexedDB/oemDB';

// Default branding (NuagePOS Eagle)
const DEFAULT_BRANDING: OEMConfig = {
  oemId: 'default',
  brandName: 'NuagePOS Eagle',
  customDomain: 'default',
  contactInfo: {
    address: '',
    phone: '',
    email: 'support@nuagepos.com',
  },
  translations: {
    en: {
      brandName: 'NuagePOS Eagle',
      welcomeMessage: 'Welcome to NuagePOS Eagle',
    },
    fr: {
      brandName: 'NuagePOS Eagle',
      welcomeMessage: 'Bienvenue à NuagePOS Eagle',
    },
  },
  createdAt: Date.now(),
  updatedAt: Date.now(),
  active: true,
};

/**
 * Extract domain from current URL
 */
export const getCurrentDomain = (): string => {
  if (typeof window === 'undefined') return 'localhost';
  
  const hostname = window.location.hostname;
  
  // Handle localhost and development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'localhost';
  }
  
  return hostname;
};

/**
 * Detect OEM configuration from current domain
 * Offline-first: Check IndexedDB first, then Firestore
 */
export const detectOEMFromDomain = async (): Promise<OEMConfig> => {
  const domain = getCurrentDomain();
  console.log('[OEM] Detecting OEM for domain:', domain);
  
  // For localhost/development, return default branding
  if (domain === 'localhost') {
    console.log('[OEM] Localhost detected - using default branding');
    return DEFAULT_BRANDING;
  }

  try {
    // Step 1: Check IndexedDB cache first (offline-first)
    await oemDB.init();
    const cachedOEM = await oemDB.getOEMByDomain(domain);
    
    if (cachedOEM && cachedOEM.active) {
      console.log('[OEM] Config loaded from IndexedDB:', cachedOEM.brandName, 'for domain:', domain);
      
      // Background refresh from Firestore (non-blocking)
      refreshOEMFromFirestore(domain).catch(err => 
        console.warn('[OEM] Background refresh failed:', err)
      );
      
      return cachedOEM;
    } else {
      console.log('[OEM] No cached OEM found in IndexedDB for domain:', domain);
    }

    // Step 2: Fetch from Firestore if not in cache
    console.log('[OEM] Fetching from Firestore for domain:', domain);
    const firestoreOEM = await fetchOEMFromFirestore(domain);
    
    if (firestoreOEM) {
      console.log('[OEM] Found in Firestore:', firestoreOEM.brandName);
      // Cache in IndexedDB for offline use
      await oemDB.saveOEMConfig(firestoreOEM);
      
      // Download logo if available
      if (firestoreOEM.logo?.url) {
        console.log('[OEM] Downloading logo from:', firestoreOEM.logo.url);
        downloadAndCacheLogo(firestoreOEM.oemId, firestoreOEM.logo.url).catch(err =>
          console.warn('[OEM] Logo download failed:', err)
        );
      }
      
      return firestoreOEM;
    }

    // Step 3: Return default branding if no OEM found
    console.warn('[OEM] No OEM found for domain:', domain, '- Using default branding');
    return DEFAULT_BRANDING;

  } catch (error) {
    console.error('[OEM] Error detecting OEM:', error);
    return DEFAULT_BRANDING;
  }
};

/**
 * Fetch OEM configuration from Firestore by domain
 */
const fetchOEMFromFirestore = async (domain: string): Promise<OEMConfig | null> => {
  try {
    console.log('[OEM] Querying Firestore for customDomain:', domain);
    const oemsRef = collection(db, 'oems');
    const q = query(oemsRef, where('customDomain', '==', domain), where('active', '==', true));
    const querySnapshot = await getDocs(q);

    console.log('[OEM] Firestore query returned', querySnapshot.size, 'documents');
    
    if (!querySnapshot.empty) {
      const oemDoc = querySnapshot.docs[0];
      const data = oemDoc.data() as OEMConfig;
      console.log('[OEM] Found OEM in Firestore:', data.brandName, 'with domain:', data.customDomain);
      return data;
    }

    console.warn('[OEM] No matching OEM found in Firestore for domain:', domain);
    return null;
  } catch (error) {
    console.error('[OEM] Error fetching OEM from Firestore:', error);
    return null;
  }
};

/**
 * Background refresh OEM config from Firestore
 */
const refreshOEMFromFirestore = async (domain: string): Promise<void> => {
  const firestoreOEM = await fetchOEMFromFirestore(domain);
  
  if (firestoreOEM) {
    await oemDB.saveOEMConfig(firestoreOEM);
    
    // Update logo if changed
    if (firestoreOEM.logo?.url) {
      const cachedLogo = await oemDB.getOEMLogo(firestoreOEM.oemId);
      if (!cachedLogo || cachedLogo.uploadedAt < firestoreOEM.logo.uploadedAt) {
        await downloadAndCacheLogo(firestoreOEM.oemId, firestoreOEM.logo.url);
      }
    }
  }
};

/**
 * Download logo from URL and cache in IndexedDB
 */
export const downloadAndCacheLogo = async (oemId: string, logoUrl: string): Promise<void> => {
  try {
    console.log('[OEM] Attempting to download logo from:', logoUrl);
    
    // Use fetch with mode: 'cors' to handle cross-origin requests
    const response = await fetch(logoUrl, {
      mode: 'cors',
      credentials: 'omit',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to download logo: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const mimeType = blob.type || 'image/png';
    
    console.log('[OEM] Logo downloaded successfully, size:', blob.size, 'type:', mimeType);
    
    await oemDB.saveOEMLogo(oemId, blob, mimeType);
    console.log('[OEM] Logo cached for OEM:', oemId);
  } catch (error) {
    console.error('[OEM] Error downloading logo:', error);
    // Don't throw - allow app to continue with fallback logo
  }
};

/**
 * Get OEM logo as data URL for display
 * Falls back to direct URL if IndexedDB cache fails
 */
export const getOEMLogoDataURL = async (oemId: string): Promise<string | null> => {
  try {
    // Try to get from IndexedDB cache first
    const logoData = await oemDB.getOEMLogo(oemId);
    
    if (logoData) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(logoData.logoBlob);
      });
    }
    
    // If not in cache, try to get URL from OEM config
    const oemConfig = await oemDB.getOEMConfig(oemId);
    if (oemConfig?.logo?.url) {
      console.log('[OEM] Using direct logo URL (not cached):', oemConfig.logo.url);
      return oemConfig.logo.url;
    }
    
    return null;
  } catch (error) {
    console.error('[OEM] Error reading logo:', error);
    
    // Last resort: try to get URL from config
    try {
      const oemConfig = await oemDB.getOEMConfig(oemId);
      if (oemConfig?.logo?.url) {
        console.log('[OEM] Fallback to direct logo URL:', oemConfig.logo.url);
        return oemConfig.logo.url;
      }
    } catch (fallbackError) {
      console.error('[OEM] Fallback also failed:', fallbackError);
    }
    
    return null;
  }
};

/**
 * Get OEM by ID (for admin/settings)
 */
export const getOEMById = async (oemId: string): Promise<OEMConfig | null> => {
  try {
    // Check IndexedDB first
    await oemDB.init();
    const cachedOEM = await oemDB.getOEMConfig(oemId);
    
    if (cachedOEM) return cachedOEM;

    // Fetch from Firestore
    const oemDoc = await getDoc(doc(db, 'oems', oemId));
    
    if (oemDoc.exists()) {
      const oemData = oemDoc.data() as OEMConfig;
      await oemDB.saveOEMConfig(oemData);
      return oemData;
    }

    return null;
  } catch (error) {
    console.error('Error fetching OEM by ID:', error);
    return null;
  }
};

/**
 * Get all OEMs (for admin list)
 */
export const getAllOEMs = async (): Promise<OEMConfig[]> => {
  try {
    // Try IndexedDB first for offline access
    await oemDB.init();
    const cachedOEMs = await oemDB.getAllOEMConfigs();
    
    if (cachedOEMs.length > 0) {
      // Background refresh
      refreshAllOEMsFromFirestore().catch(err => 
        console.warn('Background OEM list refresh failed:', err)
      );
      return cachedOEMs;
    }

    // Fetch from Firestore
    return await fetchAllOEMsFromFirestore();
  } catch (error) {
    console.error('Error fetching all OEMs:', error);
    return [];
  }
};

/**
 * Fetch all OEMs from Firestore
 */
const fetchAllOEMsFromFirestore = async (): Promise<OEMConfig[]> => {
  try {
    const oemsRef = collection(db, 'oems');
    const querySnapshot = await getDocs(oemsRef);
    
    const oems: OEMConfig[] = [];
    querySnapshot.forEach(doc => {
      oems.push(doc.data() as OEMConfig);
    });

    // Cache all in IndexedDB
    for (const oem of oems) {
      await oemDB.saveOEMConfig(oem);
    }

    return oems;
  } catch (error) {
    console.error('Error fetching OEMs from Firestore:', error);
    return [];
  }
};

/**
 * Background refresh all OEMs
 */
const refreshAllOEMsFromFirestore = async (): Promise<void> => {
  await fetchAllOEMsFromFirestore();
};

/**
 * Get default branding
 */
export const getDefaultBranding = (): OEMConfig => {
  return { ...DEFAULT_BRANDING };
};
